import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronDown, BookOpen, Layers, ArrowRight } from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────────────────
export interface CourseLesson {
    lesson_id: number;
    title: string;
}
export interface CourseModule {
    module_id: number;
    title: string;
    lessons: CourseLesson[];
}
export interface CourseDetail {
    name: string;
    tierColor: string;
    badge: string;
    emoji: string;
    modules: CourseModule[];
}
interface Props {
    course: CourseDetail | null;
    onClose: () => void;
    onEnrol?: () => void;
}

// ── All 30 courses — module + lesson titles sourced from classes.module_plan ──
export const ELITE_COURSE_DETAILS: Record<string, CourseModule[]> = {
    // ── FOUNDATION ─────────────────────────────────────────────────────────────
    'Etiquette & Social Grace': [
        { module_id: 80100101, title: 'Table Manners & Dining Basics', lessons: [
            { lesson_id: 8010010101, title: 'Sitting at the Table Like a Gentleman or Lady' },
            { lesson_id: 8010010102, title: 'Using Cutlery Correctly' },
            { lesson_id: 8010010103, title: 'Being a Good Guest' },
        ]},
        { module_id: 80100102, title: 'Greeting People & Making Introductions', lessons: [
            { lesson_id: 8010010201, title: 'The Perfect Handshake and Greeting' },
            { lesson_id: 8010010202, title: 'Please, Thank You, and Excuse Me' },
            { lesson_id: 8010010203, title: 'Kind Words and How to Be a Good Friend' },
        ]},
        { module_id: 80100103, title: 'Posture, Appearance & Public Behaviour', lessons: [
            { lesson_id: 8010010301, title: 'Standing Tall and Walking with Confidence' },
            { lesson_id: 8010010302, title: 'Being Respectful in Public Spaces' },
            { lesson_id: 8010010303, title: 'Dress and Personal Presentation' },
        ]},
        { module_id: 80100104, title: 'Speaking Kindly and Listening Carefully', lessons: [
            { lesson_id: 8010010401, title: 'How to Listen Like a Champion' },
            { lesson_id: 8010010402, title: 'Speaking Clearly and Politely' },
            { lesson_id: 8010010403, title: 'Saying Sorry and Forgiving Others' },
        ]},
    ],
    'Foundational Elocution & Vocal Mechanics': [
        { module_id: 2020101, title: 'The Instrument of Voice', lessons: [
            { lesson_id: 202010101, title: 'Posture and Presence' },
            { lesson_id: 202010102, title: 'Diaphragmatic Breathing' },
            { lesson_id: 202010103, title: 'Volume and Projection' },
        ]},
        { module_id: 2020102, title: 'Precision Diction & Clarity', lessons: [
            { lesson_id: 202010201, title: 'Biting the Consonants' },
            { lesson_id: 202010202, title: 'Sculpting the Vowels' },
            { lesson_id: 202010203, title: 'Banishing Mumbles and Fillers' },
        ]},
        { module_id: 2020103, title: 'Expression and Storytelling', lessons: [
            { lesson_id: 202010301, title: 'Pacing and Speed' },
            { lesson_id: 202010302, title: 'The Power of the Pause' },
            { lesson_id: 202010303, title: 'Facial Expressions and Emotion' },
        ]},
        { module_id: 2020104, title: 'Performance and Connection', lessons: [
            { lesson_id: 202010401, title: 'Eye Contact and Reading the Room' },
            { lesson_id: 202010402, title: 'Reading Aloud with Brilliance' },
            { lesson_id: 202010403, title: 'The Polite Introduction & First Impressions' },
        ]},
    ],
    'Presence & Social Poise': [
        { module_id: 8020101, title: 'The Invisible Language of the Body', lessons: [
            { lesson_id: 802010101, title: 'The Golden Thread — Master Your Posture' },
            { lesson_id: 802010102, title: 'Walking with Purpose' },
            { lesson_id: 802010103, title: 'Sitting, Standing, and Transitions' },
        ]},
        { module_id: 8020102, title: 'The Power of the Gaze', lessons: [
            { lesson_id: 802010201, title: 'Eye Contact — The Bridge of Trust' },
            { lesson_id: 802010202, title: 'Facial Expressions and Social Warmth' },
            { lesson_id: 802010203, title: 'The Active Listening Face' },
        ]},
        { module_id: 8020103, title: 'Hands and Gestures', lessons: [
            { lesson_id: 802010301, title: 'The Home Base — What to Do With Your Hands' },
            { lesson_id: 802010302, title: 'Elegant Gestures and Pointing' },
            { lesson_id: 802010303, title: 'Banishing Fidgeting and Nervous Tics' },
        ]},
        { module_id: 8020104, title: 'Spatial Awareness and Boundaries', lessons: [
            { lesson_id: 802010401, title: 'The Social Sphere and Personal Space' },
            { lesson_id: 802010402, title: 'Navigating Crowds and Doorways' },
            { lesson_id: 802010403, title: 'Stillness as the Ultimate Strength' },
        ]},
    ],
    'Art History & Culture': [
        { module_id: 3030101, title: 'The Language of Art', lessons: [
            { lesson_id: 303010101, title: 'Seeing vs. Looking' },
            { lesson_id: 303010102, title: 'The Magic of the Color Wheel' },
            { lesson_id: 303010103, title: 'Three-Dimensional Magic (Sculpture)' },
        ]},
        { module_id: 3030102, title: 'Meet the Masterpieces', lessons: [
            { lesson_id: 303010201, title: 'Leonardo and The Mona Lisa' },
            { lesson_id: 303010202, title: 'Vincent van Gogh and Starry Night' },
            { lesson_id: 303010203, title: 'The Power of Architecture' },
        ]},
        { module_id: 3030103, title: 'The Magic of Classical Music', lessons: [
            { lesson_id: 303010301, title: 'Inside the Symphony Orchestra' },
            { lesson_id: 303010302, title: 'The Giants — Mozart and Beethoven' },
            { lesson_id: 303010303, title: 'Introduction to the Opera' },
        ]},
        { module_id: 3030104, title: 'Elite Cultural Etiquette', lessons: [
            { lesson_id: 303010401, title: 'Museum and Gallery Manners' },
            { lesson_id: 303010402, title: 'The Concert Hall Rules' },
            { lesson_id: 303010403, title: 'Discussing Art with Elegance' },
        ]},
    ],
    'Financial Acumen': [
        { module_id: 990101, title: 'The Philosophy of Wealth & Value', lessons: [
            { lesson_id: 99010101, title: 'What is True Wealth?' },
            { lesson_id: 99010102, title: 'The History of Money and Banks' },
            { lesson_id: 99010103, title: 'The Superpower of Delayed Gratification' },
        ]},
        { module_id: 990102, title: 'The Elite Three-Jar System', lessons: [
            { lesson_id: 99010201, title: 'The Saving Jar (Paying Yourself First)' },
            { lesson_id: 99010202, title: 'The Spending Jar (Calculated Joy)' },
            { lesson_id: 99010203, title: 'The Giving Jar (Stewardship)' },
        ]},
        { module_id: 990103, title: 'Earning, Labor, and Entrepreneurship', lessons: [
            { lesson_id: 99010301, title: 'Solving Problems for Profit' },
            { lesson_id: 99010302, title: 'The Dignity of Hard Work' },
            { lesson_id: 99010303, title: 'Fair Trades and Mutual Benefit' },
        ]},
        { module_id: 990104, title: 'Stewardship, Debt, and Generational Thinking', lessons: [
            { lesson_id: 99010401, title: 'Protecting Physical Assets (Stewardship)' },
            { lesson_id: 99010402, title: 'The Danger of Debt and Borrowing' },
            { lesson_id: 99010403, title: 'Building a Family Legacy' },
        ]},
    ],
    'Logic & Negotiation': [
        { module_id: 1010101, title: 'The Foundations of Clear Thinking', lessons: [
            { lesson_id: 101010101, title: 'Facts, Opinions, and The Truth' },
            { lesson_id: 101010102, title: "The 'Why' Game (Root Cause Analysis)" },
        ]},
        { module_id: 1010102, title: 'The Art of the Fair Trade', lessons: [
            { lesson_id: 101010201, title: 'What is a Win-Win?' },
            { lesson_id: 101010202, title: 'Keeping Your Cool (Emotional Regulation)' },
            { lesson_id: 101010203, title: 'Body Language in Disagreements' },
        ]},
    ],
    'Equestrian Studies': [
        { module_id: 4040101, title: 'Introduction to the Horse', lessons: [
            { lesson_id: 404010101, title: 'Equine Psychology and Safety' },
            { lesson_id: 404010102, title: 'Reading Body Language' },
            { lesson_id: 404010103, title: 'Haltering and Leading' },
        ]},
        { module_id: 4040102, title: 'Equipment and Attire', lessons: [
            { lesson_id: 404010201, title: 'The English Saddle and Bridle' },
            { lesson_id: 404010202, title: 'Proper Equestrian Attire' },
            { lesson_id: 404010203, title: 'Tacking Up and Untacking' },
        ]},
        { module_id: 4040103, title: 'The Art of Riding', lessons: [
            { lesson_id: 404010301, title: 'Mounting and The Perfect Seat' },
            { lesson_id: 404010302, title: 'The Natural Aids (Communication)' },
            { lesson_id: 404010303, title: 'The Walk and The Trot' },
        ]},
        { module_id: 4040104, title: 'Horsemanship and Care', lessons: [
            { lesson_id: 404010401, title: 'Grooming and Bonding' },
            { lesson_id: 404010402, title: 'Feeding and Nutrition' },
            { lesson_id: 404010403, title: 'Respecting the Countryside' },
        ]},
    ],
    'Classical Music & Opera': [
        { module_id: 7070101, title: 'The Magic of the Orchestra', lessons: [
            { lesson_id: 707010101, title: 'The Conductor and the Stage' },
            { lesson_id: 707010102, title: 'The Strings and Woodwinds (The Voices)' },
            { lesson_id: 707010103, title: 'The Brass and Percussion (The Thunder)' },
        ]},
        { module_id: 7070102, title: 'The Great Storytellers (Composers)', lessons: [
            { lesson_id: 707010201, title: 'J.S. Bach and Musical Math' },
            { lesson_id: 707010202, title: 'Mozart, the Magical Child' },
            { lesson_id: 707010203, title: 'Beethoven and the Storm' },
        ]},
        { module_id: 7070103, title: 'Welcome to the Opera', lessons: [
            { lesson_id: 707010301, title: 'What is an Opera? (Singing the Story)' },
            { lesson_id: 707010302, title: 'The Heroes and Villains (Vocal Types)' },
            { lesson_id: 707010303, title: 'Behind the Curtain (The Theatrical Magic)' },
        ]},
        { module_id: 7070104, title: 'The Young Patron (Concert Etiquette)', lessons: [
            { lesson_id: 707010401, title: 'Dressing for the Symphony' },
            { lesson_id: 707010402, title: 'The Golden Rule of Silence' },
            { lesson_id: 707010403, title: 'How to Applaud and Say Thank You' },
        ]},
    ],
    // ── INTERMEDIATE ───────────────────────────────────────────────────────────
    'Rhetoric & Debate': [
        { module_id: 2020201, title: 'Speech Architecture', lessons: [
            { lesson_id: 202020101, title: 'The Hook and the Opening' },
            { lesson_id: 202020102, title: 'The Body and Signposting' },
            { lesson_id: 202020103, title: 'The Conclusion and Call to Action' },
        ]},
        { module_id: 2020202, title: 'Persuasion Mechanics', lessons: [
            { lesson_id: 202020201, title: 'Ethos, Pathos, and Logos' },
            { lesson_id: 202020202, title: 'Storytelling as Evidence' },
            { lesson_id: 202020203, title: 'Introduction to Rhetorical Devices' },
        ]},
        { module_id: 2020203, title: 'Vocal and Physical Delivery', lessons: [
            { lesson_id: 202020301, title: 'Vocal Variety and Pitch' },
            { lesson_id: 202020302, title: 'Managing Adrenaline and Stage Fright' },
            { lesson_id: 202020303, title: 'Purposeful Body Language and Blocking' },
        ]},
        { module_id: 2020204, title: 'Debate and Impromptu', lessons: [
            { lesson_id: 202020401, title: 'Constructing a Rebuttal' },
            { lesson_id: 202020402, title: 'Active Listening in Debate' },
            { lesson_id: 202020403, title: 'Impromptu Speaking Frameworks' },
        ]},
    ],
    'Cultural Fluency & Context': [
        { module_id: 3030201, title: 'Historical Eras', lessons: [
            { lesson_id: 303020101, title: 'The Renaissance Rebirth' },
            { lesson_id: 303020102, title: 'Baroque Drama to Romanticism' },
            { lesson_id: 303020103, title: 'The Impressionist Rebellion' },
        ]},
        { module_id: 3030202, title: 'Decoding the Canvas', lessons: [
            { lesson_id: 303020201, title: 'Perspective and Composition' },
            { lesson_id: 303020202, title: 'Iconography and Symbolism' },
            { lesson_id: 303020203, title: 'The Mechanics of Light and Shadow' },
        ]},
        { module_id: 3030203, title: 'The Performing Arts', lessons: [
            { lesson_id: 303020301, title: 'Anatomy of a Symphony' },
            { lesson_id: 303020302, title: 'Classical Ballet and Movement' },
            { lesson_id: 303020303, title: 'The World of Opera' },
        ]},
        { module_id: 3030204, title: 'Gallery and Theater Protocol', lessons: [
            { lesson_id: 303020401, title: 'Intermission and Networking Etiquette' },
            { lesson_id: 303020402, title: 'Formulating a Brilliant Critique' },
            { lesson_id: 303020403, title: 'High Society Dress Codes & Conduct' },
        ]},
    ],
    'Capital, Markets & Enterprise': [
        { module_id: 990201, title: 'The Mechanics of Capital', lessons: [
            { lesson_id: 99020101, title: 'Assets vs. Liabilities' },
            { lesson_id: 99020102, title: 'Compound Interest (The Eighth Wonder)' },
            { lesson_id: 99020103, title: 'Inflation and Purchasing Power' },
        ]},
        { module_id: 990202, title: 'The Stock Market & Equities', lessons: [
            { lesson_id: 99020201, title: 'What is a Stock?' },
            { lesson_id: 99020202, title: 'Index Funds and Diversification' },
            { lesson_id: 99020203, title: 'Bear Markets, Bull Markets, and Psychology' },
        ]},
        { module_id: 990203, title: 'Debt & Leverage', lessons: [
            { lesson_id: 99020301, title: 'The Mechanics of Credit and Good vs. Bad Debt' },
            { lesson_id: 99020302, title: 'The Power of Leverage (OPM)' },
            { lesson_id: 99020303, title: 'Taxes and the Rules of the Game' },
        ]},
        { module_id: 990204, title: 'Business Architecture', lessons: [
            { lesson_id: 99020401, title: 'Margins, Overhead, and Cash Flow' },
            { lesson_id: 99020402, title: 'Supply, Demand, and The Moat' },
            { lesson_id: 99020403, title: 'The Pitch and The Art of Capital Raising' },
        ]},
    ],
    'Classic Literature': [
        { module_id: 8080101, title: 'The Epic & The Ancient World', lessons: [
            { lesson_id: 808010101, title: 'Homer and The Iliad (The Anatomy of Glory)' },
            { lesson_id: 808010102, title: 'The Odyssey (Strategy over Strength)' },
            { lesson_id: 808010103, title: 'Beowulf and The Anglo-Saxon Code' },
        ]},
        { module_id: 8080202, title: 'The Genius of Shakespeare', lessons: [
            { lesson_id: 808020201, title: 'The Architecture of the Globe and Iambic Pentameter' },
            { lesson_id: 808020202, title: 'Macbeth and the Psychology of Ambition' },
            { lesson_id: 808020203, title: "A Midsummer Night's Dream (Comedy and Chaos)" },
        ]},
        { module_id: 8080303, title: 'The 19th Century Novel & Social Critique', lessons: [
            { lesson_id: 808030301, title: 'Charles Dickens and the Weaponization of Empathy' },
            { lesson_id: 808030302, title: "Mary Shelley's Frankenstein (The Limits of Science)" },
            { lesson_id: 808030303, title: 'Mark Twain and the Power of Satire' },
        ]},
        { module_id: 8080404, title: 'Literary Critique & Annotation', lessons: [
            { lesson_id: 808040401, title: 'How to Read as a Predator (Marginalia & Active Reading)' },
            { lesson_id: 808040402, title: 'Identifying Themes, Motifs, and Symbols' },
            { lesson_id: 808040403, title: 'How to Write a Flawless Literary Argument' },
        ]},
    ],
    'Presence & Command: The Art of Leadership': [
        { module_id: 2020501, title: 'The Biology of Presence', lessons: [
            { lesson_id: 202050101, title: 'Stillness and the Physics of Power' },
            { lesson_id: 202050102, title: 'The Gravity of a Leader' },
            { lesson_id: 202050103, title: 'Spatial Ownership and Thresholds' },
        ]},
        { module_id: 2020502, title: 'The Architecture of Authority', lessons: [
            { lesson_id: 202050201, title: 'The Non-Verbal Dictionary' },
            { lesson_id: 202050202, title: 'Eye Contact as a Social Weapon' },
            { lesson_id: 202050203, title: 'The Choreography of the Hands' },
        ]},
        { module_id: 2020503, title: 'Social Magnetism & Energy Dynamics', lessons: [
            { lesson_id: 202050301, title: 'Charisma and Narrative Energy' },
            { lesson_id: 202050302, title: "Mastering the Room's Temperature" },
            { lesson_id: 202050303, title: 'The Art of Active Charm and Mirroring' },
        ]},
        { module_id: 2020504, title: 'Performance Mastery & The Unseen Leader', lessons: [
            { lesson_id: 202050401, title: 'Adrenaline to Fuel (Stage Fright & Biology)' },
            { lesson_id: 202050402, title: 'The Graceful Exit and Leaving a Mark' },
            { lesson_id: 202050403, title: 'The Synthesis of Command (Capstone)' },
        ]},
    ],
    // ── ADVANCED ────────────────────────────────────────────────────────────────
    'Global Oratory': [
        { module_id: 2020301, title: 'Advanced Rhetoric & Cadence', lessons: [
            { lesson_id: 202030101, title: 'The Rhythm of Leadership (Advanced Devices)' },
            { lesson_id: 202030102, title: 'Metaphor, Framing, and Cognitive Linguistics' },
            { lesson_id: 202030103, title: 'Writing for the Ear vs. the Eye' },
        ]},
        { module_id: 2020302, title: 'High-Stakes Environments', lessons: [
            { lesson_id: 202030201, title: 'Diplomatic Protocol and Model UN Speeches' },
            { lesson_id: 202030202, title: 'Boardroom Briefings and Executive Summaries' },
            { lesson_id: 202030203, title: 'Crisis Communications' },
        ]},
        { module_id: 2020303, title: 'Technical Delivery', lessons: [
            { lesson_id: 202030301, title: 'Teleprompter Mastery' },
            { lesson_id: 202030302, title: 'Microphones, Cameras, and Lighting' },
            { lesson_id: 202030303, title: 'The Soundbite and Media Training' },
        ]},
        { module_id: 2020304, title: 'Hostile Interrogation & Defense', lessons: [
            { lesson_id: 202030401, title: 'Navigating Hostile Q&A' },
            { lesson_id: 202030402, title: 'The Pivot and Logical Aikido' },
            { lesson_id: 202030403, title: 'The Ethics of Persuasion vs. Propaganda' },
        ]},
    ],
    'The Global Citizen - Diplomacy & Protocol': [
        { module_id: 880101, title: 'High-Level Networking & Room Dynamics', lessons: [
            { lesson_id: 88010101, title: 'Commanding a Room and Formal Introductions' },
            { lesson_id: 88010102, title: 'The Art of the Exit and Follow-Up' },
            { lesson_id: 88010103, title: 'Navigating Elite Galas and VIP Events' },
        ]},
        { module_id: 880202, title: 'Cross-Cultural Diplomacy & Global Protocol', lessons: [
            { lesson_id: 88020201, title: 'Global Greetings and Gestures' },
            { lesson_id: 88020202, title: 'International Dining and Gift-Giving' },
            { lesson_id: 88020203, title: 'Hosting International Guests' },
        ]},
        { module_id: 880303, title: 'Elite Courting & Vetting a Life Partner', lessons: [
            { lesson_id: 88030301, title: 'The Etiquette of Formal Courting' },
            { lesson_id: 88030302, title: 'Vetting Character and Defeating Predators' },
            { lesson_id: 88030303, title: 'Building a Generational Alliance' },
        ]},
        { module_id: 880404, title: 'High-Society Conflict & Boardroom Decorum', lessons: [
            { lesson_id: 88040401, title: 'Managing Conflict and Grace Under Pressure' },
            { lesson_id: 88040402, title: 'Philanthropic Etiquette and Boardroom Behavior' },
            { lesson_id: 88040403, title: 'The Architecture of Reputation and Crisis' },
        ]},
    ],
    'Advanced Financial Engineering & Wealth Mechanics': [
        { module_id: 9090101, title: 'Corporate Architecture & Asset Protection', lessons: [
            { lesson_id: 909010101, title: 'Entities (LLCs, C-Corps, Holding Companies)' },
            { lesson_id: 909010102, title: 'The Corporate Veil and Liability Shielding' },
            { lesson_id: 909010103, title: 'Tax Havens, Trusts, and Offshore Mechanics' },
        ]},
        { module_id: 9090202, title: 'Venture Capital & Startup Mechanics', lessons: [
            { lesson_id: 909020201, title: 'The VC Power Law and the Startup Pitch' },
            { lesson_id: 909020202, title: 'Equity, Dilution, and the Cap Table' },
            { lesson_id: 909020203, title: 'Term Sheets and Board Control' },
        ]},
        { module_id: 9090303, title: 'Private Equity & Mergers', lessons: [
            { lesson_id: 909030301, title: 'The Leveraged Buyout (LBO)' },
            { lesson_id: 909030302, title: 'Restructuring and Operational Ruthlessness' },
            { lesson_id: 909030303, title: 'M&A, The IPO, and The Exit' },
        ]},
        { module_id: 9090404, title: 'Wall Street & The Global Markets', lessons: [
            { lesson_id: 909040401, title: 'The Stock Market, Shorting, and Squeezes' },
            { lesson_id: 909040402, title: 'Hedge Funds, Quants, and Arbitrage' },
            { lesson_id: 909040403, title: 'The Bond Market, Interest Rates, and Macroeconomics' },
        ]},
    ],
    'Generational Wealth & The Family Office': [
        { module_id: 990301, title: 'Advanced Asset Allocation & Leverage', lessons: [
            { lesson_id: 99030101, title: 'Portfolio Theory and Alternative Assets' },
            { lesson_id: 99030102, title: 'The Strategic Use of Debt and Leverage' },
            { lesson_id: 99030103, title: 'Advanced Real Estate Strategies' },
        ]},
        { module_id: 990302, title: 'Stewardship and Legal Architecture', lessons: [
            { lesson_id: 99030201, title: 'The Family Office (Structure & Operations)' },
            { lesson_id: 99030202, title: 'Irrevocable Trusts and Estate Taxes' },
            { lesson_id: 99030203, title: 'High-Level Philanthropy & Impact Investing' },
        ]},
        { module_id: 990303, title: 'Navigating Succession', lessons: [
            { lesson_id: 99030301, title: 'Handing Down the Family Office' },
            { lesson_id: 99030302, title: 'Governance, Advisory Boards & Family Constitutions' },
            { lesson_id: 99030303, title: 'Preparing the Next Generation' },
        ]},
        { module_id: 990304, title: "The Dynasty Builder's Philosophy", lessons: [
            { lesson_id: 99030401, title: 'Multi-Generational Time Horizons' },
            { lesson_id: 99030402, title: 'Managing Inherited Wealth Psychologically' },
            { lesson_id: 99030403, title: 'The Capstone: Your 100-Year Plan' },
        ]},
    ],
    'High Culture, Curation & The Art Market': [
        { module_id: 3030301, title: 'Modernism & Architecture', lessons: [
            { lesson_id: 303030101, title: 'Cubism to Abstract Expressionism' },
            { lesson_id: 303030102, title: 'The Ideology of Architecture' },
            { lesson_id: 303030103, title: 'Contemporary Art and Installations' },
        ]},
        { module_id: 3030302, title: 'The Global Art Market', lessons: [
            { lesson_id: 303030201, title: 'Valuation and Provenance' },
            { lesson_id: 303030202, title: 'The Mechanics of the Auction House' },
            { lesson_id: 303030203, title: 'Forgery and Authentication' },
        ]},
        { module_id: 3030303, title: 'Patronage & Philanthropy', lessons: [
            { lesson_id: 303030301, title: 'The Philanthropic Board of Directors' },
            { lesson_id: 303030302, title: 'Commissioning and Managing Collections' },
            { lesson_id: 303030303, title: 'The Gala, The Premiere, and Soft Power' },
        ]},
        { module_id: 3030304, title: 'Cultural Diplomacy', lessons: [
            { lesson_id: 303030401, title: 'Soft Power and Statecraft' },
            { lesson_id: 303030402, title: 'Repatriation and the Ethics of Heritage' },
            { lesson_id: 303030403, title: "The Global Collector's Vision" },
        ]},
    ],
    'Bloodstock & Elite Pursuits': [
        { module_id: 4040301, title: 'Elite Equestrian Competition', lessons: [
            { lesson_id: 404030101, title: 'Three-Day Eventing and FEI Standards' },
            { lesson_id: 404030102, title: 'The Game of Polo (Strategy and Protocol)' },
            { lesson_id: 404030103, title: 'The Psychology of the Elite Rider' },
        ]},
        { module_id: 4040302, title: 'Bloodstock and Equine Economics', lessons: [
            { lesson_id: 404030201, title: 'Thoroughbred Breeding and Pedigrees' },
            { lesson_id: 404030202, title: 'The Economics of Syndicates' },
            { lesson_id: 404030203, title: 'Navigating the Bloodstock Auction' },
        ]},
        { module_id: 4040303, title: 'Estate Management & Rural Economics', lessons: [
            { lesson_id: 404030301, title: 'Land Stewardship and Conservation Law' },
            { lesson_id: 404030302, title: 'Tenant Relations and Rural Economics' },
            { lesson_id: 404030303, title: 'Forestry, Agriculture, and Sustainability' },
        ]},
        { module_id: 4040304, title: 'The Global Sporting Calendar & Protocol', lessons: [
            { lesson_id: 404030401, title: 'Royal Ascot and Racing Etiquette' },
            { lesson_id: 404030402, title: 'The Grouse Moor and Driven Shooting' },
            { lesson_id: 404030403, title: 'Networking and Soft Power in the Countryside' },
        ]},
    ],
    'Sartorial Intelligence & Social Camouflage': [
        { module_id: 5050101, title: 'The Psychology of Style', lessons: [
            { lesson_id: 505010101, title: 'Quiet Luxury vs. Loud Insecurity' },
            { lesson_id: 505010102, title: 'Social Camouflage and Room Dynamics' },
            { lesson_id: 505010103, title: 'The High-Low Method (Budget Fluidity)' },
        ]},
        { module_id: 5050202, title: 'The Architecture of Tailoring', lessons: [
            { lesson_id: 505020201, title: 'The Geometry of Fit' },
            { lesson_id: 505020202, title: 'Fabrics, Textiles, and Seasons' },
            { lesson_id: 505020203, title: 'Maintenance, Preservation, and Packing' },
        ]},
        { module_id: 5050303, title: 'High Society Dress Codes', lessons: [
            { lesson_id: 505030301, title: 'Deconstructing Business and Smart Casual' },
            { lesson_id: 505030302, title: 'Cocktail, Black Tie, and White Tie Protocol' },
            { lesson_id: 505030303, title: 'Grooming, Scent, and Physical Presence' },
        ]},
        { module_id: 5050404, title: 'The Economics of Elite Accessories', lessons: [
            { lesson_id: 505040401, title: 'Horology (The Rules of Elite Watches)' },
            { lesson_id: 505040402, title: 'Leather Goods, Luggage, and Footwear' },
            { lesson_id: 505040403, title: 'Building the 20-Year Capsule Wardrobe' },
        ]},
    ],
    'Wine Appreciation & Sommelier Basics': [
        { module_id: 6060101, title: 'The Foundations of Viticulture & Tasting', lessons: [
            { lesson_id: 606010101, title: 'The Geography and Terroir of Wine' },
            { lesson_id: 606010102, title: 'The Science of Vinification (Winemaking)' },
            { lesson_id: 606010103, title: 'The Deductive Tasting Method (Sight, Nose, Palate)' },
        ]},
        { module_id: 6060202, title: 'The Noble Grapes & Global Regions', lessons: [
            { lesson_id: 606020201, title: 'The Kings of France (Bordeaux vs. Burgundy)' },
            { lesson_id: 606020202, title: 'The Masterpieces of Italy & Spain' },
            { lesson_id: 606020203, title: 'The New World Challengers (USA, South America, Oceania)' },
        ]},
        { module_id: 6060303, title: "The Sommelier's Protocol (Service & Etiquette)", lessons: [
            { lesson_id: 606030301, title: 'Navigating the Restaurant Wine List' },
            { lesson_id: 606030302, title: 'The Mechanics of Proper Wine Service' },
            { lesson_id: 606030303, title: 'The Art of Food and Wine Pairing' },
        ]},
        { module_id: 6060404, title: 'Cellar Management & The Business of Wine', lessons: [
            { lesson_id: 606040401, title: 'Building and Maintaining a Blue-Chip Cellar' },
            { lesson_id: 606040402, title: 'The Global Fine Wine Market and Auctions' },
            { lesson_id: 606040403, title: 'Hosting a Formal Tasting and Toasting Etiquette' },
        ]},
    ],
    'Classical Music & Opera Appreciation': [
        { module_id: 7070101, title: 'The Architecture of the Orchestra', lessons: [
            { lesson_id: 707010101, title: 'Harmonic Language, Counterpoint & The Architecture of Composition' },
            { lesson_id: 707010102, title: 'Advanced String & Woodwind Technique — Beyond the Basics' },
            { lesson_id: 707010103, title: 'Brass, Acoustic Physics & The Engineering of the Concert Hall' },
        ]},
        { module_id: 7070202, title: 'The Titans of Classical Music (Eras & Geniuses)', lessons: [
            { lesson_id: 707020201, title: 'The Baroque Era (Mathematical Precision)' },
            { lesson_id: 707020202, title: 'The Classical Era (Elegant Balance)' },
            { lesson_id: 707020203, title: 'The Romantic Era (Emotional Rebellion)' },
        ]},
        { module_id: 7070303, title: 'The Grandeur of the Opera House', lessons: [
            { lesson_id: 707030301, title: 'Anatomy of an Opera (Gesamtkunstwerk)' },
            { lesson_id: 707030302, title: 'Vocal Classifications and Bel Canto' },
            { lesson_id: 707030303, title: 'The Italian Masters (Verdi, Puccini, & Verismo)' },
        ]},
        { module_id: 7070404, title: 'Elite Engagement & Wagnerian Epics', lessons: [
            { lesson_id: 707040401, title: 'Richard Wagner and The Ring Cycle' },
            { lesson_id: 707040402, title: 'The Ballet and The Russian Rebellion' },
            { lesson_id: 707040403, title: 'Concert Hall Protocol and Intellectual Critique' },
        ]},
    ],
    'Philanthropy & Legacy Building': [
        { module_id: 9091101, title: 'The Philosophy and Ethics of Wealth Allocation', lessons: [
            { lesson_id: 909110101, title: 'The Moral Imperative of Capital' },
            { lesson_id: 909110102, title: 'Venture Philanthropy and Effective Altruism' },
            { lesson_id: 909110103, title: "The Ethics of Power and the 'Savior Complex'" },
        ]},
        { module_id: 9091202, title: 'Strategic Impact and Global Initiatives', lessons: [
            { lesson_id: 909120201, title: 'Eradicating Systemic Agony' },
            { lesson_id: 909120202, title: 'Crisis and Disaster Philanthropy' },
            { lesson_id: 909120203, title: 'Environmental Stewardship & Planetary Legacy' },
        ]},
        { module_id: 9091303, title: 'Building the Institutional Legacy', lessons: [
            { lesson_id: 909130301, title: 'The Architecture of a Family Foundation' },
            { lesson_id: 909130302, title: 'The Power of Endowments and Educational Immortality' },
            { lesson_id: 909130303, title: 'Patronage of the Arts and Pure Sciences' },
        ]},
        { module_id: 9091404, title: 'The Capstone of Character', lessons: [
            { lesson_id: 909140401, title: 'Reputational Capital and the Family Name' },
            { lesson_id: 909140402, title: 'Mentorship and Human Capital' },
            { lesson_id: 909140403, title: 'The Final Audit (Writing Your Own Eulogy)' },
        ]},
    ],
    'Luxury Travel & International Etiquette': [
        { module_id: 8080101, title: 'The Architecture of Elite Logistics (Aviation & Transit)', lessons: [
            { lesson_id: 808010101, title: 'Private Aviation and The FBO' },
            { lesson_id: 808010102, title: 'First-Class Commercial and Terminal Protocol' },
            { lesson_id: 808010103, title: 'Ground Transportation and The Chauffeur' },
        ]},
        { module_id: 8080202, title: 'High-End Hospitality & The Concierge', lessons: [
            { lesson_id: 808020201, title: 'The 5-Star Hotel Ecosystem' },
            { lesson_id: 808020202, title: "The Clefs d'Or Concierge" },
            { lesson_id: 808020203, title: 'Private Villas and Estate Rentals' },
        ]},
        { module_id: 8080303, title: 'Global Security, Discretion, and The Entourage', lessons: [
            { lesson_id: 808030301, title: 'Travel Security and K&R (Kidnap & Ransom) Awareness' },
            { lesson_id: 808030302, title: 'Managing the Entourage and Staff' },
            { lesson_id: 808030303, title: 'Digital Discretion and Privacy Abroad' },
        ]},
        { module_id: 8080404, title: 'The Jet-Set Lifestyle & Exclusive Enclaves', lessons: [
            { lesson_id: 808040401, title: 'Yachting Protocol and Maritime Etiquette' },
            { lesson_id: 808040402, title: "Navigating Private Members' Clubs Globally" },
            { lesson_id: 808040403, title: 'The Global Circuit and Cultural Chameleonism' },
        ]},
    ],
    'Executive Time Management & Deep Work': [
        { module_id: 9095101, title: 'The Architecture of Time & The War on Attention', lessons: [
            { lesson_id: 909510101, title: 'The Mathematics of Mortality and Time Valuation' },
            { lesson_id: 909510102, title: 'The Attention Economy and Digital Fasting' },
            { lesson_id: 909510103, title: 'Biological Prime Time and Energy Allocation' },
        ]},
        { module_id: 9095202, title: 'Deep Work & Cognitive Dominance', lessons: [
            { lesson_id: 909520201, title: 'The Philosophy of Deep Work' },
            { lesson_id: 909520202, title: 'Entering and Sustaining the Flow State' },
            { lesson_id: 909520203, title: 'Embracing Boredom and Strategic Solitude' },
        ]},
        { module_id: 9095303, title: 'Elite Execution & Delegation Frameworks', lessons: [
            { lesson_id: 909530301, title: 'The Eisenhower Matrix and Ruthless Prioritization' },
            { lesson_id: 909530302, title: "The Art of the Absolute 'No'" },
            { lesson_id: 909530303, title: 'Delegation and the Executive Assistant (EA)' },
        ]},
        { module_id: 9095404, title: 'Systems, Automation, & The Executive Operating System', lessons: [
            { lesson_id: 909540401, title: 'Calendar Sovereignty and Time Blocking' },
            { lesson_id: 909540402, title: 'Meeting Eradication and Asynchronous Work' },
            { lesson_id: 909540403, title: 'The Weekly Review and The Executive Operating System' },
        ]},
    ],
    'Health, Nutrition & Vitality': [
        { module_id: 120101, title: 'The Architecture of Sleep and Recovery', lessons: [
            { lesson_id: 12010101, title: 'The Neuroscience of Sleep and Circadian Rhythm' },
            { lesson_id: 12010102, title: 'Autonomic Regulation and Active Recovery' },
            { lesson_id: 12010103, title: 'The Psychology of Burnout and Sustained Execution' },
        ]},
        { module_id: 120202, title: 'Elite Nutritional Biochemistry', lessons: [
            { lesson_id: 12020201, title: 'Macronutrient Architecture and Metabolic Flexibility' },
            { lesson_id: 12020202, title: 'Micronutrients, Supplementation, and Cellular Health' },
            { lesson_id: 12020203, title: 'The Etiquette and Strategy of High-Performance Dining' },
        ]},
        { module_id: 120303, title: 'Kinetic Mastery and Physical Conditioning', lessons: [
            { lesson_id: 12030301, title: 'Biomechanics, Posture, and Injury Prevention' },
            { lesson_id: 12030302, title: 'Strength, VO2 Max, and Cardiovascular Dominance' },
            { lesson_id: 12030303, title: 'The Philosophy of Elite Sports and Aristocratic Athleticism' },
        ]},
        { module_id: 120404, title: 'The Psychology of Peak Performance and Longevity', lessons: [
            { lesson_id: 12040401, title: 'Somatic Discipline and the Mind-Body Axis' },
            { lesson_id: 12040402, title: 'Biohacking, Longevity, and the Ethics of Enhancement' },
            { lesson_id: 12040403, title: 'The Symphony of Vitality (The 50-Year Protocol)' },
        ]},
    ],
};

// ── Design tokens ─────────────────────────────────────────────────────────────
const DARK = '#0A0A0F';
const CARD = '#1A1A24';
const DARK3 = '#16161F';

// ── Accordion module row ──────────────────────────────────────────────────────
function ModuleRow({ mod, color, index }: { mod: CourseModule; color: string; index: number }) {
    const [open, setOpen] = useState(index === 0);
    return (
        <div className="rounded-2xl overflow-hidden border border-white/5" style={{ background: CARD }}>
            <button
                onClick={() => setOpen(o => !o)}
                className="w-full flex items-center justify-between px-5 py-4 text-left transition-all hover:bg-white/5 active:scale-[0.99]"
            >
                <div className="flex items-center gap-3 min-w-0">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 text-xs font-black"
                        style={{ background: `${color}20`, color }}>
                        {index + 1}
                    </div>
                    <span className="text-sm font-bold text-white/90 leading-snug">{mod.title}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-3">
                    <span className="text-[11px] font-medium text-white/40">{mod.lessons.length} lessons</span>
                    <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}>
                        <ChevronDown size={16} className="text-white/30" />
                    </motion.div>
                </div>
            </button>

            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.22, ease: 'easeInOut' }}
                        className="overflow-hidden"
                    >
                        <div className="px-5 pb-4 pt-3 space-y-1.5 border-t border-white/5">
                            {mod.lessons.map((lesson, li) => (
                                <div key={lesson.lesson_id} className="flex items-start gap-3 py-1.5">
                                    <div className="w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-black shrink-0 mt-0.5"
                                        style={{ background: `${color}15`, color }}>
                                        {li + 1}
                                    </div>
                                    <span className="text-xs sm:text-sm text-white/70 leading-relaxed font-medium">{lesson.title}</span>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

// ── Modal ─────────────────────────────────────────────────────────────────────
export default function CourseDetailModal({ course, onClose, onEnrol }: Props) {
    const totalLessons = course?.modules.reduce((s, m) => s + m.lessons.length, 0) ?? 0;

    return (
        <AnimatePresence>
            {course && (
                <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 md:p-6 overflow-hidden">
                    {/* Backdrop */}
                    <motion.div
                        key="backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/80 backdrop-blur-md"
                    />

                    {/* Dialog Card: Mobile Bottom Sheet / Desktop Centered Modal */}
                    <motion.div
                        key="panel"
                        initial={{ opacity: 0, y: 30, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 30, scale: 0.96 }}
                        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
                        className="relative z-10 w-full max-w-2xl lg:max-w-3xl rounded-t-[2rem] sm:rounded-3xl border border-white/10 shadow-2xl flex flex-col max-h-[88dvh] sm:max-h-[85vh] overflow-hidden"
                        style={{
                            background: DARK3,
                            boxShadow: `0 24px 80px rgba(0,0,0,0.8), 0 0 40px ${course.tierColor}15`,
                        }}
                    >
                        {/* Mobile drag handle indicator */}
                        <div className="flex sm:hidden justify-center pt-3 pb-1 shrink-0 bg-[#0E0E14]">
                            <div className="w-10 h-1 rounded-full bg-white/20" />
                        </div>

                        {/* Sticky Header */}
                        <div className="px-5 sm:px-8 py-5 border-b border-white/5 shrink-0 bg-[#0E0E14]">
                            <div className="flex items-start justify-between gap-4">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                                        <span
                                            className="text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full"
                                            style={{ color: course.tierColor, background: `${course.tierColor}18` }}
                                        >
                                            {course.badge}
                                        </span>
                                        <span className="text-white/40 text-sm">{course.emoji}</span>
                                    </div>
                                    <h2 className="text-xl sm:text-2xl font-black text-white leading-tight mb-2">
                                        {course.name}
                                    </h2>
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-semibold text-white/40">
                                        {course.modules.length > 0 ? (
                                            <>
                                                <span className="flex items-center gap-1.5 text-white/60">
                                                    <Layers size={13} style={{ color: course.tierColor }} />
                                                    {course.modules.length} modules
                                                </span>
                                                <span>·</span>
                                                <span className="flex items-center gap-1.5 text-white/60">
                                                    <BookOpen size={13} style={{ color: course.tierColor }} />
                                                    {totalLessons} lessons
                                                </span>
                                                <span className="hidden sm:inline">·</span>
                                                <span className="hidden sm:inline text-white/40">20 core concepts each · mastery quiz</span>
                                            </>
                                        ) : (
                                            <span className="text-white/40">Loading syllabus…</span>
                                        )}
                                    </div>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-all hover:bg-white/10 active:scale-90 border border-white/10"
                                    style={{ background: 'rgba(255,255,255,0.06)' }}
                                    aria-label="Close course detail"
                                >
                                    <X size={18} className="text-white/60 hover:text-white" />
                                </button>
                            </div>
                        </div>

                        {/* Scrollable body */}
                        <div className="overflow-y-auto flex-1 px-4 sm:px-8 py-5 space-y-3 overscroll-contain">
                            {course.modules.length === 0 ? (
                                <div className="py-16 text-center text-white/40 text-sm">
                                    <div className="inline-block w-5 h-5 border-2 border-white/20 border-t-white/80 rounded-full animate-spin mb-3" />
                                    <p className="font-semibold text-white/60">Loading curriculum syllabus…</p>
                                </div>
                            ) : (
                                course.modules.map((mod, i) => (
                                    <ModuleRow key={mod.module_id} mod={mod} color={course.tierColor} index={i} />
                                ))
                            )}
                        </div>

                        {/* Sticky Footer CTA */}
                        <div className="px-5 sm:px-8 py-4 border-t border-white/10 bg-[#0C0C12] shrink-0">
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                                <div className="text-center sm:text-left">
                                    <p className="text-white font-bold text-sm">Ready to master this curriculum?</p>
                                    <p className="text-white/40 text-xs hidden sm:block">Full interactive dialogue, scenario simulation & mastery quizzes.</p>
                                </div>
                                <button
                                    onClick={() => {
                                        onClose();
                                        onEnrol?.();
                                    }}
                                    className="w-full sm:w-auto px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 transition-all hover:opacity-95 active:scale-95 shadow-lg shrink-0"
                                    style={{
                                        background: `linear-gradient(135deg, ${course.tierColor}, #ffffff)`,
                                        color: '#0A0A0F',
                                        boxShadow: `0 8px 24px ${course.tierColor}30`
                                    }}
                                >
                                    <span>Start this course</span>
                                    <ArrowRight size={14} />
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
